/**
 * Conversations waiting on a person: an approval, an unread mention, a
 * handoff nobody picked up. Rows open the thread in its workspace.
 */
import { ArrowRightLeft, AtSign, BellRing, Clock } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";

import { relativeTime } from "@/features/agents/lib/time";

import type { AttentionReason, AttentionThreadRow } from "../../types";
import { CardRow, DashboardCard } from "./DashboardCard";

const REASON_ICON: Record<AttentionReason, typeof Clock> = {
  approval: Clock,
  mentions: AtSign,
  handoff: ArrowRightLeft
};

type Props = {
  items: AttentionThreadRow[] | undefined;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /** Where "View all" goes (the agent workspace). */
  onViewAll?: () => void;
  span?: "full";
};

export const AttentionCard: FC<Props> = ({
  items,
  isLoading,
  error,
  onRetry,
  onViewAll,
  span
}) => {
  const navigate = useNavigate();
  const rows = items ?? [];
  return (
    <DashboardCard
      icon={BellRing}
      title="Attention"
      count={rows.length}
      onViewAll={onViewAll}
      viewAllLabel="Open Workspace"
      isLoading={isLoading && !items}
      error={error}
      onRetry={onRetry}
      isEmpty={rows.length === 0}
      emptyTitle="Nothing Needs You"
      emptyDescription="Approvals, mentions and handoffs show up here."
      span={span}
      testId="attention-card"
    >
      {rows.map((row) => {
        const Icon = REASON_ICON[row.reason] ?? Clock;
        return (
          <CardRow
            key={row.thread_id}
            onClick={() => navigate(row.path)}
            leading={<Icon className="size-3.5 text-muted-foreground" />}
            primary={row.title}
            secondary={`${row.detail}${
              row.updated_at ? ` · ${relativeTime(row.updated_at)}` : ""
            }`}
            testId="attention-row"
          />
        );
      })}
    </DashboardCard>
  );
};
